import { Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DPFormField } from 'src/app/core/models/DPFormField';
import { DPFormFieldType } from 'src/app/core/models/enums/DPFormFieldType';

import { CheckboxComponent } from '../fields/checkbox/checkbox.component';
import { DateComponent } from '../fields/date/date.component';
import { DropdownComponent } from '../fields/dropdown/dropdown.component';
import { RadioGroupComponent } from '../fields/radio-group/radio-group.component';
import { MultiLineComponent } from '../fields/multi-line/multi-line.component';
import { MultivalueComponent } from '../fields/multivalue/multivalue.component';
import { SingleLineComponent } from '../fields/single-line/single-line.component';
import { StartEndDateComponent } from '../fields/start-end-date/start-end-date.component';
import { YesnoFieldComponent } from '../fields/yesno-field/yesno-field.component';
import { FieldErrorComponent } from '../field-error/field-error.component';
import { AddressComponent } from '../fields/address/address.component';

/**
 * Dynamic field component.
 * 
 * This component renders correct field component based on field type.
 */
@Component({
  selector: 'app-dynamic-field',
  standalone: true,
  imports: [
    AddressComponent,
    CheckboxComponent,
    DateComponent,
    DropdownComponent,
    RadioGroupComponent,
    MultiLineComponent,
    MultivalueComponent,
    SingleLineComponent,
    StartEndDateComponent,
    YesnoFieldComponent,
    FieldErrorComponent,
  ],
  templateUrl: './dynamic-field.component.html',
  styleUrls: ['./dynamic-field.component.scss'],
})
export class DynamicFieldComponent implements OnInit {
  // Form field object
  @Input() field: DPFormField;

  // Angular form control object
  @Input() control: UntypedFormControl;

  // Field disabled state
  @Input() set disabled(val: boolean) {
    /**
     * Set disabled state to Angular Form control object.
     */
    if (val) {
      this.control?.disable({ onlySelf: true, emitEvent: false });
    } else {
      this.control?.enable({ onlySelf: true, emitEvent: false });
    }

    this.fieldDisabled = val;
  }

  // This subject is to send markAsTouched event from parent form to individual field component.
  // Used in multivalue form.
  @Input() markAsTouched: Subject<void>;

  // Subfields. Used in multivalue and yes/no fields.
  @Input() subFields: DPFormField[] = [];

  @Input() customColumnTpls: Record<string, TemplateRef<any>>;

  // OnUpdate event emitted if the field is multivalue
  @Output() onUpdate = new EventEmitter();

  fieldDisabled: boolean;
  DPFormFieldType = DPFormFieldType;
  unsubscribe$ = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    if (this.subFields && this.field.options) {
      /**
       * Implementation logic for trigger fields.
       * 
       * If a field is radio field or yes/no field and has subfields,
       * those subfields are displayed depending on option selected.
       * 
       * Which option will display subfields is determined by subQuestionTriggerOptionId value in subfields.
       */
      const optionIDs = this.field.options.map((opt) => opt.key.toString());
      const shouldTriggerSubfields = this.subFields.some(
        (subField) => {
          const triggerOptionIds = (subField.subQuestionTriggerOptionId || '').toString().split(',');
          return triggerOptionIds.some(optionId => optionIDs.includes(optionId));
        }
      );

      /**
       * If current field is the trigger of subfields,
       * listen for the value changes and run validation on subfields.
       * 
       * If a subfield is not displayed, all validators of the subfield is skipped.
       */
      if (shouldTriggerSubfields) {
        this.control.valueChanges
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(() => {
            // Intentional timeout for control updates to take effect.
            setTimeout(() => {
              this.subFields.forEach((subField) => {
                const control: AbstractControl =
                  this.control.parent.controls[subField.name];
                if (control) {
                  control.updateValueAndValidity();
                }
              });
            });
          });
      }
    }

    /**
     * When the control changes, mark the parent as touched.
     * 
     * TODO: re-evaluate if this is required.
     */
    this.control?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.control.parent.markAsTouched();
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
